/**
 * JSON-LD FAQPage Schema Generator
 * 
 * Generates Google-compliant FAQPage structured data
 * for SEO rich results
 */

import { getFAQsForPage } from './faqContent';

/**
 * Generate FAQPage JSON-LD schema
 * @param {Array} faqs - Array of FAQ items with { question, answer }
 * @param {string} pageUrl - Canonical URL of the page
 * @returns {Object} FAQPage schema object
 */
export const generateFAQSchema = (faqs, pageUrl) => {
  if (!faqs || faqs.length === 0) {
    return null;
  }

  const mainEntity = faqs.map((faq, index) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": typeof faq.answer === 'string' ? faq.answer : 
              (faq.answer?.props?.children || String(faq.answer))
    }
  }));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": mainEntity
  };
};

/**
 * Generate FAQPage schema with additional metadata
 * @param {Array} faqs - Array of FAQ items
 * @param {Object} options - Additional options
 * @param {string} options.pageUrl - Canonical URL
 * @param {string} options.pageTitle - Page title
 * @param {string} options.pageDescription - Page description
 * @returns {Object} Enhanced FAQPage schema
 */
export const generateEnhancedFAQSchema = (faqs, options = {}) => {
  const { pageUrl, pageTitle, pageDescription } = options;
  
  const baseSchema = generateFAQSchema(faqs, pageUrl);
  
  if (!baseSchema) {
    return null;
  }

  // Add page metadata if provided
  if (pageUrl) {
    baseSchema.url = pageUrl;
  }

  if (pageTitle) {
    baseSchema.headline = pageTitle;
  }

  if (pageDescription) {
    baseSchema.description = pageDescription;
  }

  return baseSchema;
};

/**
 * Convert FAQ schema to JSON-LD script tag content
 * @param {Object} schema - FAQPage schema object
 * @returns {string} JSON string for script tag
 */
export const schemaToJSONLD = (schema) => {
  if (!schema) {
    return null;
  }
  
  return JSON.stringify(schema, null, 2);
};

/**
 * Generate FAQ schema for a specific page type
 * @param {string} pageType - Page type (landing, dashboard, pricing, etc.)
 * @param {string} pageUrl - Canonical URL
 * @param {Object} options - Additional options
 * @returns {Object|null} FAQPage schema or null
 */
export const generatePageFAQSchema = (pageType, pageUrl, options = {}) => {
  const faqs = getFAQsForPage(pageType);
  
  if (!faqs || faqs.length === 0) {
    return null;
  }

  return generateEnhancedFAQSchema(faqs, {
    pageUrl,
    ...options
  });
};
